import { TypedUseQueryHookResult } from "@reduxjs/toolkit/query/react";
import { useCallback } from "react";

import { dataSourceIdMap } from "features/data-source/data-source-id-map";
import { DataSourceName, DataSourceParameterMap } from "features/data-source/data-source.types";
import { useDataSourceOldApproach } from "features/data-source/useDataSourceOldApproach";
import { useHasUserArea } from "hooks/useHasUserArea";
import {
    GetDataSourceOptionsV1,
    useGetDataSourceQuery as useGetDataSourceQueryJSBE,
    useLazyGetDataSourceQuery as useLazyGetDataSourceQueryJSBE,
} from "store/services/data-source.service";
import {
    DataSourceParameter,
    GetDataSourceDeprecatedOptions,
    useGetDataSourceQuery as useGetDataSourceQueryCSBE,
    useLazyGetDataSourceQuery as useLazyGetDataSourceQueryCSBE,
} from "store/services/deprecated/data-source.service";

export const useDataSource = <T = any>(
    dataSource: DataSourceName,
    parameters?: DataSourceParameterMap<any>,
    options?: { skip?: boolean }
) => {
    return useDataSourceOldApproach<T>(dataSource, parameters, options);
};

export const useLazyDataSource = <T = any>(dataSource: DataSourceName) => {
    // Checks which data source query endpoint (C# vs JS) to call based on area
    const hasJSBEDataSourcesEnabled = useHasUserArea(["Refactor_JSBEDataSources"]);

    const [_triggerJSBE, resultJSBE] = useLazyGetDataSourceQueryJSBE();
    const [_triggerCSBE, resultCSBE] = useLazyGetDataSourceQueryCSBE();

    const trigger = useCallback(
        (parameters?: DataSourceParameter[], preferCacheValue?: boolean) => {
            if (hasJSBEDataSourcesEnabled) {
                // Always use v1 endpoint for JSBE
                return _triggerJSBE(
                    {
                        apiVersion: "v1",
                        id: dataSourceIdMap[dataSource],
                        parameters,
                    } as GetDataSourceOptionsV1,
                    preferCacheValue
                );
            } else {
                // Use CSBE endpoint
                return _triggerCSBE(
                    {
                        id: dataSourceIdMap[dataSource],
                        parameters,
                    } as GetDataSourceDeprecatedOptions,
                    preferCacheValue
                );
            }
        },
        [hasJSBEDataSourcesEnabled, _triggerJSBE, _triggerCSBE, dataSource]
    );

    const result = hasJSBEDataSourcesEnabled ? resultJSBE : resultCSBE;

    return [trigger, result] as [typeof trigger, TypedUseQueryHookResult<T, any, any>];
};

export const toDataSourceParameters = <T extends Record<string, any>>(parameters?: T): DataSourceParameter[] => {
    if (!parameters) return [];
    let dataSourceParameters = Object.keys(parameters).map((parameter) => ({
        name: parameter,
        value: parameters[parameter] ?? "",
    }));

    dataSourceParameters = dataSourceParameters.toSorted((a, b) => a.name.localeCompare(b.name));

    return dataSourceParameters;
};
